// One row per artifact structure (§26 F6) — every kind, the Well included, so
// what is authorable is testable.
//
// Each row is a *grant* button, never a switch: the power is on while the
// structure stands, so the lamp on the left reports the board and the click
// puts a token in hand. Placing is the hotbar's token tile: arm it, then click
// bare ground you hold.

// What stays on the engine: **the table of artifact kinds and their powers**
// — which structures are artifacts, what each is called and drawn as, and the
// power it grants. The contract has no `catalog.structures()` yet; a power is
// on while one stands under your control (`hasPower`), which is counted off
// `game.board.facts()` here rather than asked.
import { ARTIFACT_KINDS } from "src/engine/rules/industry/state";
import { STRUCTURE_NAMES } from "src/engine/rules/industry/structures";
import { POWER_KIND, POWER_LIST } from "src/engine/rules/campaign/powers";
import { STRUCTURE_GLYPH } from "src/engine/session/glyphs";
import { STRUCTURE_KIND } from "src/client/ai/render/factNames";
import type { DevDispatch } from "../DevMenu";
import { Btn, HoverCard, styled, Text } from "../kit";
import { useGame } from "../useGame";
import { useSessionView } from "../useSessionView";
import { useBesidePanel, Fold } from "./parts";

/** A row: lamp, name, grant. Drawn once per kind, so a `styled` and not `sx`. */
const Row = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.sm,
  padding: `${theme.kit.space.xs}px ${theme.kit.space.sm}px`,
  borderRadius: theme.kit.radius.sm,
  border: `2px solid ${on ? theme.kit.color.brand : theme.kit.color.line}`,
  background: `color-mix(in srgb, ${theme.kit.color.panelRaised} 85%, transparent)`
}));

/** Lit while a structure of this kind stands under the run side's control. */
const Lamp = styled("span", {
  shouldForwardProp: (prop: string) => prop !== "on"
})<{ on?: boolean }>(({ theme, on }) => ({
  width: 10,
  height: 10,
  flexShrink: 0,
  borderRadius: "50%",
  background: on ? theme.kit.color.brand : theme.kit.color.line
}));

const Name = styled(Text)({ flexGrow: 1 });

export function ArtifactTokens({
  dispatch,
  side
}: {
  dispatch: DevDispatch;
  /** The run's own side — the tokens are the player's. */
  side: number;
}) {
  const game = useGame();
  // What is in hand is the hotbar's token tiles; what stands is on the board.
  const { hotbar } = useSessionView();
  const standingByKind = new Map<number, number>();
  for (const sq of game.board.facts().squares.values()) {
    const structure = sq.structure;
    if (!structure || structure.owner !== side) continue;
    const kind = STRUCTURE_KIND[structure.kind];
    standingByKind.set(kind, (standingByKind.get(kind) ?? 0) + 1);
  }
  const panel = useBesidePanel();

  return (
    <Fold id="tokens" title="Artifact tokens">
      {ARTIFACT_KINDS.map((kind) => {
        const power = POWER_LIST.find((p) => POWER_KIND[p.id] === kind);
        const standing = standingByKind.get(kind) ?? 0;
        // **A power is on while a structure of its kind stands under your
        // control** (`hasPower`) — the same count, read off the facts.
        const on = power ? standing > 0 : false;
        const held = hotbar.tokens.find((tile) => STRUCTURE_KIND[tile.structure] === kind)?.count ?? 0;
        return (
          <Row key={kind} on={on}>
            <HoverCard
              placement="right"
              beside={panel}
              title={power?.name ?? STRUCTURE_NAMES[kind]}
              card={
                power
                  ? on
                    ? `On — ${standing} standing.`
                    : "Off — nothing standing."
                  : "No power yet (A3's)."
              }
            >
              <Lamp on={on} />
            </HoverCard>
            <Name weight={700} size={12} tone={on ? "brand" : "ink"}>
              {STRUCTURE_GLYPH[kind]} {STRUCTURE_NAMES[kind]}
              {standing > 0 ? ` · ${standing} up` : ""}
              {held > 0 ? ` · ${held} in hand` : ""}
            </Name>
            <HoverCard
              placement="right"
              beside={panel}
              title="Grant a token"
              card="Puts one in hand — place it with the token tile on the hotbar."
            >
              <Btn
                small
                variant="ghost"
                data-testid={`dev-token-${kind}`}
                onClick={() => dispatch({ type: "grant-token", kind })}
              >
                + token
              </Btn>
            </HoverCard>
          </Row>
        );
      })}
    </Fold>
  );
}
